import { DropdownOption } from 'components/Common/ValidatedDropdown';
import topicOptions from 'views/ContactNoteForm/components/data/topicOptions';
import initiatedByOptions from 'views/ContactNoteForm/components/data/initiatedByOptions';
import sourceOptions from 'views/ContactNoteForm/components/data/sourceOptions';
import { isArray } from 'rxjs/internal-compatibility';
import { EnvConfig } from 'config/envConfig';

export enum TextTransformAction {
  ENUM_TO_SENTENCE_CASE = 'ENUM_TO_STRING',
  BOOLEAN_TO_YES_NO_STRING = 'BOOLEAN_TO_YES_NO_STRING',
  DATE_TO_STRING = 'DATE_TO_STRING',
  ARRAY_TO_STRING = 'ARRAY_TO_STRING',
  ENUM_ARRAY_TO_STRING = 'ENUM_ARRAY_TO_STRING',
  NONE = 'NONE',
}

/**
 * Transform a string with the defined actions
 * @param stringToTransform The string to transform
 * @param action The transformation
 */
export function convertText(stringToTransform: string | any[], action: TextTransformAction[]) {
  if (action.includes(TextTransformAction.NONE)) {
    return stringToTransform;
  }
  if (action.includes(TextTransformAction.ENUM_ARRAY_TO_STRING) && Array.isArray(stringToTransform)) {
    stringToTransform = enumArrayToString(stringToTransform as []);
  }
  if (action.includes(TextTransformAction.ARRAY_TO_STRING) && Array.isArray(stringToTransform)) {
    stringToTransform = arrayToCommaSeparatedString(stringToTransform as []);
  }
  if (action.includes(TextTransformAction.ENUM_TO_SENTENCE_CASE)) {
    stringToTransform = findOrFormatEnum(stringToTransform as string);
  }
  if (action.includes(TextTransformAction.BOOLEAN_TO_YES_NO_STRING)) {
    stringToTransform = booleanToYesNo(stringToTransform);
  }
  if (action.includes(TextTransformAction.DATE_TO_STRING)) {
    stringToTransform = isDate(stringToTransform as string) ? formatDate(stringToTransform as string) : '-';
  }
  return stringToTransform;
}

/**
 * Convert a snake-case ENUM value (e.g. "TAX_RESIDENCE, DOMICILE") to sentence case ("Tax residence, Domicile")
 * @param enumString the enum to convert
 */
export function findOrFormatEnum(enumString: string): string {
  const foundEnum = findEnumObject(enumString);
  if (foundEnum && foundEnum.label) {
    return foundEnum.label;
  }
  return formatEnum(enumString);
}

/**
 * Convert a snake-case ENUM value (e.g. "TAX_RESIDENCE, DOMICILE") to sentence case ("Tax residence, Domicile")
 * @param enumString the enum to convert
 */
export function formatEnum(enumString: string): string {
  if (!enumString) {
    return '-';
  }
  if (EnvConfig.config.createContactNoteNewUiEnabled) {
    return (
      enumString.charAt(0) +
      enumString
        .slice(1)
        .replaceAll('_', ' ')
        .replaceAll(/,\s*([a-z])/g, function (d, e) {
          return ', ' + e.toUpperCase();
        })
    );
  }
  return (
    enumString.charAt(0) +
    enumString
      .slice(1)
      .replaceAll('_', ' ')
      .toLowerCase()
      .replaceAll(/,\s*([a-z])/g, function (d, e) {
        return ', ' + e.toUpperCase();
      })
  );
}

const findEnumObject = (enumeration: string) => {
  const initiatedByPlaceholderEnum = initiatedByOptions.find((currentEnum) => currentEnum.value === enumeration);
  if (initiatedByPlaceholderEnum) {
    return initiatedByPlaceholderEnum;
  }
  const topicPlaceholderEnum = topicOptions.find((currentEnum) => currentEnum.value === enumeration);
  if (topicPlaceholderEnum) {
    return topicPlaceholderEnum;
  }
  const sourceOptionsEnum = sourceOptions.find((currentEnum) => currentEnum.value === enumeration);
  if (sourceOptionsEnum) {
    return sourceOptionsEnum;
  }
  return null;
};

/**
 * Convert a raw date string (e.g "2020-05-06T10:46:46.283+0000") to legible date and time format (08.06.2020 17:36)
 * @param date the date string to convert
 */
export function formatDateTime(date: string): string {
  let finalDateTime = date;
  if (date) {
    const updateDateTime = new Date(date);
    const submittedDate = formatDate(date);
    const submittedTime = updateDateTime.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
    });
    finalDateTime = `${submittedDate}  ${submittedTime}`;
  }
  return finalDateTime;
}

/**
 * Convert a raw date string (e.g "2020-05-06T10:46:46.283+0000") to legible date format (06.05.2020.)
 * @param date the enum to convert
 */
export function formatDate(date: string): string {
  return new Date(date).toLocaleDateString('en-CH');
}

export function isDigit(value: string): boolean {
  return /^\d+$/.test(value ?? '');
}

/**
 * Check if a string represents a date.
 * @param value the the string to check.
 */
export function isDate(value: string): boolean {
  return !Number.isNaN(new Date(value).valueOf()) && value.length > 4;
}

/**
 * Translate a boolean to Yes/No. Return "-" if the string is empty or null.
 * @param value the the string to check.
 */
export function booleanToYesNo(value: any): string {
  if (value === null || value === '') {
    return '-';
  }
  return value === true || value === 'true' ? 'Yes' : 'No';
}

/**
 * Convert an array with enums to comma-separated strings
 * @param ar the array to convert
 */
export const enumArrayToString = (ar: any[]): string => {
  return ar.map((element) => findOrFormatEnum(element)).join(', ');
};

/**
 * Convert an array to comma-separated strings
 * @param ar the array to convert
 */
export const arrayToCommaSeparatedString = (ar: any[]): string => {
  return isArray(ar) ? ar.join(', ') : '-';
};

/**
 * Find the labels of an array of values from a dropdown option array
 * @param values the values to find the labels of
 * @param optionArray the array that holds all the possible label-value pairs
 */
export const findLabelByValues = (values: any, optionArray: DropdownOption[]) => {
  if (typeof values === 'string' || values instanceof String) {
    return findLabelByValue(values, optionArray);
  }
  if (!values || !optionArray || values === '') {
    return '-';
  }
  return values.map((value: string) => {
    return optionArray.find((option: DropdownOption) => option.value === value)?.label;
  });
};

/**
 * Find the label of a value from a dropdown option array
 * @param value the values to find the labels of
 * @param optionArray the array that holds all the possible label-value pairs
 */
export const findLabelByValue = (value: any, optionArray: DropdownOption[]) => {
  if (!value || !optionArray || value === '') {
    return '-';
  }
  const result = optionArray.find((option: DropdownOption) => option.value === value);
  return (result && result.label) ?? '-';
};

/**
 * Remove all space and special characters from a string
 * @param inputString the string to transform
 */
export const removeWhitespaceSpecialCharacters = (inputString: string) => {
  return inputString.replaceAll(/[^A-Z0-9]/gi, '');
};

/**
 * Checks if the given object is undefined/null/its lengh <= 0
 * @param val the object to be checked
 */
export const isEmpty = (val: any) => {
  return val === undefined || val == null || val.length <= 0 || val === '';
};

/**
 * Check if value exists in an object and is not an empty string
 * @param values the object that need to be checked
 * @param props the fields to check in the object
 */
export const areStringValuesNotEmpty = (values: any, props: string[]) => {
  return (
    props.filter((property) => !isEmpty(values[property]) && values[property].toString() !== '').length === props.length
  );
};

/**
 * Check if a string ends with any of an array of given suffixes
 * @param suffixes the list of endings
 * @param string the string to search in
 */
export const endsWithAny = (suffixes: string[], string: string): boolean => {
  return suffixes.some((suffix: string): boolean => string.endsWith(suffix));
};

/**
 * Convert kebab case string to normal string with uppercase first letter
 * @param kebabCase string in kebab case format
 */
export const kebabCaseToString = (kebabCase: string) => {
  const stringArray = kebabCase.split('-');
  stringArray[0] = stringArray[0].charAt(0).toUpperCase() + stringArray[0].slice(1);
  return stringArray.join(' ');
};
